import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const WatchlistContext = createContext();

export const useWatchlist = () => {
  const context = useContext(WatchlistContext);
  if (!context) {
    throw new Error('useWatchlist must be used within a WatchlistProvider');
  }
  return context;
};

export const WatchlistProvider = ({ children }) => {
  const [watchlist, setWatchlist] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load data from localStorage on component mount
  useEffect(() => {
    loadWatchlist();
    loadFavorites();
  }, []);

  const loadWatchlist = () => {
    try {
      const storedWatchlist = localStorage.getItem('watchlist');
      if (storedWatchlist) {
        setWatchlist(JSON.parse(storedWatchlist));
      }
    } catch (error) {
      console.error('Error loading watchlist:', error);
    }
  };

  const loadFavorites = () => {
    try {
      const storedFavorites = localStorage.getItem('favorites');
      if (storedFavorites) {
        setFavorites(JSON.parse(storedFavorites));
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const addToWatchlist = (item) => {
    try {
      setLoading(true);
      console.log('Adding to watchlist:', item);
      console.log('Current watchlist:', watchlist);
      
      const existingItem = watchlist.find(w => w.id === item.id && w.type === item.type);
      
      if (existingItem) {
        console.log('Item already exists in watchlist');
        toast.warning(`${item.title} is already in your watchlist`);
        return false;
      }

      const newItem = {
        ...item,
        addedAt: new Date().toISOString(),
        type: item.type || (item.title ? 'movie' : 'tv') // Determine type if not provided
      };

      console.log('New item to add:', newItem);
      const updatedWatchlist = [...watchlist, newItem];
      console.log('Updated watchlist:', updatedWatchlist);
      
      setWatchlist(updatedWatchlist);
      localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
      
      console.log('Successfully added to watchlist and localStorage');
      toast.success(`${item.title} added to watchlist`);
      return true;
    } catch (error) {
      console.error('Error adding to watchlist:', error);
      toast.error('Failed to add to watchlist');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeFromWatchlist = (itemId, itemType = 'movie') => {
    try {
      setLoading(true);
      const updatedWatchlist = watchlist.filter(
        item => !(item.id === itemId && item.type === itemType)
      );
      setWatchlist(updatedWatchlist);
      localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
      
      toast.success('Removed from watchlist');
      return true;
    } catch (error) {
      console.error('Error removing from watchlist:', error);
      toast.error('Failed to remove from watchlist');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const addToFavorites = (item) => {
    try {
      setLoading(true);
      console.log('Adding to favorites:', item);
      console.log('Current favorites:', favorites);
      
      const existingItem = favorites.find(f => f.id === item.id && f.type === item.type);
      
      if (existingItem) {
        console.log('Item already exists in favorites');
        toast.warning(`${item.title} is already in your favorites`);
        return false;
      }

      const newItem = {
        ...item,
        addedAt: new Date().toISOString(),
        type: item.type || (item.title ? 'movie' : 'tv')
      };

      console.log('New item to add to favorites:', newItem);
      const updatedFavorites = [...favorites, newItem];
      console.log('Updated favorites:', updatedFavorites);
      
      setFavorites(updatedFavorites);
      localStorage.setItem('favorites', JSON.stringify(updatedFavorites));
      
      console.log('Successfully added to favorites and localStorage');
      toast.success(`${item.title} added to favorites`);
      return true;
    } catch (error) {
      console.error('Error adding to favorites:', error);
      toast.error('Failed to add to favorites');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeFromFavorites = (itemId, itemType = 'movie') => {
    try {
      setLoading(true);
      const updatedFavorites = favorites.filter(
        item => !(item.id === itemId && item.type === itemType)
      );
      setFavorites(updatedFavorites);
      localStorage.setItem('favorites', JSON.stringify(updatedFavorites));
      
      toast.success('Removed from favorites');
      return true;
    } catch (error) {
      console.error('Error removing from favorites:', error);
      toast.error('Failed to remove from favorites');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const isInWatchlist = (itemId, itemType = 'movie') => {
    return watchlist.some(item => item.id === itemId && item.type === itemType);
  };

  const isInFavorites = (itemId, itemType = 'movie') => {
    return favorites.some(item => item.id === itemId && item.type === itemType);
  };

  const toggleWatchlist = (item) => {
    console.log('WatchlistContext: toggleWatchlist called with item:', item);
    const itemType = item.type || (item.title ? 'movie' : 'tv');
    console.log('WatchlistContext: itemType determined as:', itemType);
    if (isInWatchlist(item.id, itemType)) {
      console.log('WatchlistContext: Item is in watchlist, removing...');
      return removeFromWatchlist(item.id, itemType);
    } else {
      console.log('WatchlistContext: Item is not in watchlist, adding...');
      return addToWatchlist(item);
    }
  };

  const toggleFavorites = (item) => {
    console.log('WatchlistContext: toggleFavorites called with item:', item);
    const itemType = item.type || (item.title ? 'movie' : 'tv');
    console.log('WatchlistContext: itemType determined as:', itemType);
    if (isInFavorites(item.id, itemType)) {
      console.log('WatchlistContext: Item is in favorites, removing...');
      return removeFromFavorites(item.id, itemType);
    } else {
      console.log('WatchlistContext: Item is not in favorites, adding...');
      return addToFavorites(item);
    }
  };

  const clearWatchlist = () => {
    try {
      setWatchlist([]);
      localStorage.removeItem('watchlist');
      toast.success('Watchlist cleared');
    } catch (error) {
      console.error('Error clearing watchlist:', error);
      toast.error('Failed to clear watchlist');
    }
  };

  const clearFavorites = () => {
    try {
      setFavorites([]);
      localStorage.removeItem('favorites');
      toast.success('Favorites cleared');
    } catch (error) {
      console.error('Error clearing favorites:', error);
      toast.error('Failed to clear favorites');
    }
  };

  const getWatchlistCount = () => watchlist.length;
  const getFavoritesCount = () => favorites.length;

  const value = {
    // State
    watchlist,
    favorites,
    loading,
    
    // Actions
    addToWatchlist,
    removeFromWatchlist,
    addToFavorites,
    removeFromFavorites,
    toggleWatchlist,
    toggleFavorites,
    clearWatchlist,
    clearFavorites,
    
    // Utilities
    isInWatchlist,
    isInFavorites,
    getWatchlistCount,
    getFavoritesCount,
    
    // Refresh functions
    loadWatchlist,
    loadFavorites
  };

  return (
    <WatchlistContext.Provider value={value}>
      {children}
    </WatchlistContext.Provider>
  );
};
